import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createTeacherProfile, updateTeacherProfile } from '../../api/teacherProfile';
import OnboardingChatBar from '../Onboarding/OnboardingChatBar';
import styles from './WelcomePage.module.css';

const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const SUBJECTS = ['Math', 'ELA', 'Science', 'Social Studies', 'Art', 'Music', 'PE', 'World Languages'];
const SCHEDULE_TYPES = [
  { value: 'daily', label: 'Daily (same schedule every day)' },
  { value: 'block', label: 'Block (A/B day rotation)' },
  { value: 'custom', label: 'Custom' },
];
const DURATION_PRESETS = [30, 45, 50, 60, 90];
const STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming',
];

export default function WelcomePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  // Form state
  const [name, setName] = useState(user?.name || '');
  const [state, setState] = useState('');
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [scheduleType, setScheduleType] = useState('');
  const [lessonDuration, setLessonDuration] = useState(null);
  const [periodsPerDay, setPeriodsPerDay] = useState('');
  const [yearStart, setYearStart] = useState('');
  const [yearEnd, setYearEnd] = useState('');

  // Phase state
  const [phase, setPhase] = useState('form'); // 'form' | 'chat'
  const [profileId, setProfileId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const toggleGrade = (grade) => {
    setGrades((prev) =>
      prev.includes(grade) ? prev.filter((g) => g !== grade) : [...prev, grade]
    );
  };

  const toggleSubject = (subject) => {
    setSubjects((prev) =>
      prev.includes(subject) ? prev.filter((s) => s !== subject) : [...prev, subject]
    );
  };

  const isFormValid = () => {
    return (
      name.trim() &&
      state &&
      grades.length > 0 &&
      subjects.length > 0 &&
      scheduleType &&
      lessonDuration &&
      yearStart &&
      yearEnd
    );
  };

  const handleSaveAndContinue = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setSaving(true);
    setError(null);

    try {
      const profileData = {
        name: name.trim(),
        state,
        grades,
        subjects,
        scheduleType,
        lessonDuration,
        periodsPerDay: periodsPerDay ? parseInt(periodsPerDay, 10) : null,
        yearStart,
        yearEnd,
      };

      const response = await createTeacherProfile(profileData);
      const newProfileId = response.profile?.id || response.id;
      setProfileId(newProfileId);

      // Update the user's name in auth context if changed
      if (name.trim() !== user?.name) {
        updateUser({ name: name.trim() });
      }

      setPhase('chat');
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError(err.response?.data?.message || 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleFormFieldUpdate = useCallback(
    (content) => {
      if (content.onboardingComplete) {
        navigate('/dashboard');
      }
    },
    [navigate]
  );

  const handleFinish = async () => {
    if (profileId) {
      try {
        await updateTeacherProfile(profileId, { onboardingComplete: true });
      } catch {
        // Best effort — still navigate
      }
    }
    navigate('/dashboard');
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            {phase === 'form' ? 'Welcome to Teachio!' : 'Almost there!'}
          </h1>
          <p className={styles.subtitle}>
            {phase === 'form'
              ? "Let's set up your teaching profile so we can personalize your experience."
              : 'Chat with our AI to share your teaching style and preferences.'}
          </p>
        </div>

        {phase === 'form' ? (
          <form className={styles.form} onSubmit={handleSaveAndContinue}>
            {error && <div className={styles.error}>{error}</div>}

            {/* Name */}
            <div className={styles.field}>
              <label className={styles.label}>Your Name *</label>
              <input
                type="text"
                className={styles.input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Ms. Johnson"
              />
            </div>

            {/* State */}
            <div className={styles.field}>
              <label className={styles.label}>State *</label>
              <select
                className={styles.select}
                value={state}
                onChange={(e) => setState(e.target.value)}
              >
                <option value="">Select your state...</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Grades */}
            <div className={styles.field}>
              <label className={styles.label}>Grades You Teach *</label>
              <div className={styles.buttonGroup}>
                {GRADES.map((g) => (
                  <button
                    key={g}
                    type="button"
                    className={`${styles.toggleBtn} ${grades.includes(g) ? styles.toggleBtnActive : ''}`}
                    onClick={() => toggleGrade(g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            {/* Subjects */}
            <div className={styles.field}>
              <label className={styles.label}>Subjects You Teach *</label>
              <div className={styles.buttonGroup}>
                {SUBJECTS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`${styles.toggleBtn} ${subjects.includes(s) ? styles.toggleBtnActive : ''}`}
                    onClick={() => toggleSubject(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Schedule Type */}
            <div className={styles.field}>
              <label className={styles.label}>Schedule Type *</label>
              <div className={styles.buttonGroup}>
                {SCHEDULE_TYPES.map((st) => (
                  <button
                    key={st.value}
                    type="button"
                    className={`${styles.toggleBtn} ${scheduleType === st.value ? styles.toggleBtnActive : ''}`}
                    onClick={() => setScheduleType(st.value)}
                  >
                    {st.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Lesson Duration */}
            <div className={styles.field}>
              <label className={styles.label}>Lesson Duration (minutes) *</label>
              <div className={styles.buttonGroup}>
                {DURATION_PRESETS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`${styles.toggleBtn} ${lessonDuration === d ? styles.toggleBtnActive : ''}`}
                    onClick={() => setLessonDuration(d)}
                  >
                    {d} min
                  </button>
                ))}
              </div>
            </div>

            {/* Periods Per Day */}
            <div className={styles.field}>
              <label className={styles.label}>Periods Per Day</label>
              <input
                type="number"
                className={styles.input}
                value={periodsPerDay}
                onChange={(e) => setPeriodsPerDay(e.target.value)}
                placeholder="e.g. 6"
                min="1"
                max="12"
              />
            </div>

            {/* School Year Dates */}
            <div className={styles.field}>
              <label className={styles.label}>School Year *</label>
              <div className={styles.dateRow}>
                <div>
                  <label className={styles.label}>Start Date</label>
                  <input
                    type="date"
                    className={styles.input}
                    value={yearStart}
                    onChange={(e) => setYearStart(e.target.value)}
                  />
                </div>
                <div>
                  <label className={styles.label}>End Date</label>
                  <input
                    type="date"
                    className={styles.input}
                    value={yearEnd}
                    onChange={(e) => setYearEnd(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Save & Continue */}
            <button
              type="submit"
              className={styles.saveBtn}
              disabled={!isFormValid() || saving}
            >
              {saving ? 'Saving...' : 'Save & Continue'}
            </button>
          </form>
        ) : (
          <div className={styles.chatPhase}>
            <p className={styles.chatIntro}>
              Tell our AI assistant about your teaching style, classroom dynamics, and any
              preferences. This helps us personalize your lesson plans.
            </p>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleFinish}
            >
              Finish Setup
            </button>
            <button
              type="button"
              className={styles.skipBtn}
              onClick={handleFinish}
            >
              Skip for now
            </button>
          </div>
        )}
      </div>

      {phase === 'chat' && (
        <OnboardingChatBar
          mode="teacher-welcome"
          profileId={profileId}
          conversationId={conversationId}
          onConversationId={setConversationId}
          onFormFieldUpdate={handleFormFieldUpdate}
        />
      )}
    </div>
  );
}
